import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as ChevronDownIcon } from '../../../icons/chevron-down.svg';

import './select.scss';

import { Button } from '../button/Button';

export const Select = ({
  options = [],
  label,
  value,
  onChange,
  placeholder,
  required,
  icon: Icon,
  showLabel = false,
  showAsIcon = false,
  filledIcon = true,
  showSelectCount = false,
  multiSelect = false,
  action,
  onActionClick,
  dropdownWidth = 200,
  error,
  display = false
}) => {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const ref = useRef();
  const dropdownRef = useRef();

  const handleClickOutside = (e) => {
    if (
      ref.current &&
      !ref.current.contains(e.target) &&
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current?.scrollHeight ?? 200;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const spaceRight = window.innerWidth - rect.right;
      const spaceLeft = rect.left;

      const shouldAnchorTop = dropdownHeight > spaceBelow && spaceAbove > spaceBelow;
      const shouldAnchorRight = dropdownWidth > spaceRight && spaceLeft > spaceRight;

      const top = shouldAnchorTop
        ? rect.top - 8 - dropdownHeight + window.scrollY
        : rect.bottom + 8 + window.scrollY;

      const left = shouldAnchorRight
        ? rect.right - dropdownWidth + window.scrollX
        : rect.left + window.scrollX;

      setDropdownStyle({
        position: 'absolute',
        top,
        left,
        width: dropdownWidth,
        zIndex: 9999,
        background: 'white',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      });
    }
  }, [open, dropdownWidth]);

  const isSelected = (val) => (multiSelect ? value.includes(val) : value === val);

  const toggleValue = (val) => {
    if (!multiSelect) {
      onChange({ target: { value: val } });
      setOpen(false);
      return;
    }

    const newValue = value.includes(val) ? value.filter((v) => v !== val) : [...value, val];

    onChange({ target: { value: newValue } });
  };

  const renderOptions = (opts) =>
    opts.map((opt) => {
      if (opt.options) {
        return (
          <div key={opt.label} className="select__group">
            <div className="select__group-label">{opt.label}</div>
            {renderOptions(opt.options)}
          </div>
        );
      }

      return (
        <label
          key={opt.value}
          className={`select__option ${isSelected(opt.value) ? 'selected' : ''} ${multiSelect ? 'select__option--multiselect' : ''}`}
          onClick={() => toggleValue(opt.value)}
        >
          {multiSelect && (
            <input
              type="checkbox"
              checked={isSelected(opt.value)}
              onClick={(e) => e.stopPropagation()}
              readOnly
            />
          )}
          <>
            {!multiSelect && opt.icon && <opt.icon width={20} height={20} />}
            <span>{opt.label}</span>
          </>
        </label>
      );
    });

  const selectedLabel = () => {
    if (showAsIcon && multiSelect) {
      return value.length ? `${value.length}` : null;
    }

    if (multiSelect) {
      const selected = [];
      const flatten = (arr) => {
        for (const item of arr) {
          if (item.options) flatten(item.options);
          else if (value.includes(item.value)) selected.push(item.label);
        }
      };
      flatten(options);
      return selected.length ? selected.join(', ') : placeholder || 'Select';
    }

    // For single select
    const findSelectedItem = (opts) => {
      for (const item of opts) {
        if (item.options) {
          const found = findSelectedItem(item.options);
          if (found) return found;
        } else if (item.value === value) {
          return item;
        }
      }
      return null;
    };

    const selected = findSelectedItem(options);

    return (
      <>
        {selected?.icon && <selected.icon width={20} height={20} />}
        <span>{selected?.label || placeholder || 'Select'}</span>
      </>
    );
  };

  const handleOnActionClick = () => {
    onActionClick();
    setOpen(false);
  };

  const dropdownContent = (
    <div
      className={`select__dropdown ${multiSelect ? 'select__dropdown--multiselect' : ''}`}
      ref={dropdownRef}
      style={dropdownStyle}
    >
      {renderOptions(options)}
      {action && (
        <Button
          tertiary
          onClick={handleOnActionClick}
          className="select__action"
          disabled={multiSelect ? value.length === 0 : !value}
        >
          {action}
        </Button>
      )}
    </div>
  );

  return (
    <div ref={ref} className="select">
      {showLabel && (
        <span>
          {label}
          {required && !display ? <span> *</span> : ''}
        </span>
      )}
      <button
        className={`select__button ${!showAsIcon ? 'select__button--bordered' : ''} ${!!error ? 'select__button--error' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        type="button"
        title={showAsIcon ? label : null}
      >
        {showAsIcon ? (
          <div className={`select__button-icon ${filledIcon ? 'select__button-icon--filled' : ''}`}>
            {Icon && (
              <span className="select__icon">
                <Icon />
              </span>
            )}
            {selectedLabel() > 0 && showSelectCount && (
              <span className="select__count">{selectedLabel()}</span>
            )}
          </div>
        ) : (
          <div className="select__button-content">
            {Icon && (
              <span className="select__icon">
                <Icon />
              </span>
            )}
            <span className="select__label">{selectedLabel()}</span>
            {!display && (
              <span className="select__chevron">
                <ChevronDownIcon />
              </span>
            )}
          </div>
        )}
      </button>

      {open && !display && ReactDOM.createPortal(dropdownContent, document.body)}
    </div>
  );
};
